import { shellStringResources } from '../../Resources/ShellStringResources';
import { Constants } from '../../shared/shared-constants';
import { Collection, IModel, IValidationContext, IViolation } from '../base/annotation';
import { Metamodel } from '../base/model';
import { TriggerFrequency } from './schedule-trigger-properties';
import { TriggerDependency } from './trigger-dependency';
import { TriggerProperties } from './trigger-properties';
import { RetryPolicy } from './tumbling-window-retry-policy';
import moment from 'moment';

interface ITumblingWindowTriggerValidators {
    validateTriggerDependencies: (triggerProperties: TriggerProperties, context: IValidationContext) => Promise<IViolation[]>;
}

export const tumblingWindowTriggerValidators: ITumblingWindowTriggerValidators = {
    validateTriggerDependencies: undefined
};

@Metamodel.model({
    name: 'TumblingWindowTrigger',
    display: [shellStringResources.ModelName_TumblingWindowTrigger, shellStringResources.ModelName_TumblingWindowTriggers]
})
export class TumblingWindowTriggerProperties extends TriggerProperties {
    public static readonly MODEL: IModel;
    public static readonly maxHours = 1080;

    @Metamodel.attribute({
        display: shellStringResources.StartTime,
        constraint: {
            required: true
        }
    })
    public startTime: Date;

    @Metamodel.attribute({
        display: shellStringResources.EndTime,
        constraint: {
            required: false,
            validator: TumblingWindowTriggerProperties.validateEndTime
        }
    })
    public endTime?: Date;

    @Metamodel.attribute({
        display: shellStringResources.Frequency,
        constraint: {
            required: true,
            validator: TumblingWindowTriggerProperties.validateFrequency
        }
    })
    public frequency: TriggerFrequency;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_Interval,
        constraint: {
            required: true,
            whole: true,
            minimum: 1,
            validator: TumblingWindowTriggerProperties.validateInterval
        }
    })
    public interval: number;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_Delay,
        constraint: {
            required: false,
            pattern: Constants.timeoutValidation
        }
    })
    public delay: string;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_MaxConcurrency,
        constraint: {
            required: true,
            maximum: 50,
            minimum: 1,
            whole: true
        }
    })
    public maxConcurrency: number;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_RetryPolicy,
        constraint: {
            required: false
        }
    })
    public retryPolicy?: RetryPolicy;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_TumblingWindowTriggers,
        collection: Collection.Array,
        constraint: {
            minOccurs: 0,
            required: false,
            validator: TumblingWindowTriggerProperties.validateTriggerDependencies
        }
    })
    public dependsOn?: TriggerDependency[];

    public constructor() {
        super();
        this.multiplePipelines = false;
    }

    public static toHours(timespan: string): number {
        if (timespan == null) {
            return 0;
        }
        const timelist = timespan.split(/[:.]+/).reverse();
        const timescale = [24, 1, 0, 0];
        let seconds = 0;
        timelist.slice().forEach((value: string, index: number, array: string[]) => {
                seconds += Math.abs(parseInt(value, 10)) * timescale.pop();
            }
        );
        return (timespan.startsWith('-') ? -seconds : seconds);
    }

    private static validateEndTime(triggerProperties: TumblingWindowTriggerProperties, endTime?: Date): IViolation[] | undefined {
        if (endTime && moment(endTime).diff(triggerProperties.startTime) < 0) {
            return [
                {
                    message: () => shellStringResources.Trigger_EndTimeAfterStart
                }
            ];
        }
        return undefined;
    }

    private static validateFrequency(triggerProperties: TumblingWindowTriggerProperties, frequency: TriggerFrequency): IViolation[] | undefined {
        if (frequency !== 'Minute' && frequency !== 'Hour' && frequency !== 'Month') {
            return [
                {
                    message: () => shellStringResources.Trigger_InvalidFrequencyError
                }
            ];
        }
        return undefined;
    }

    private static async validateTriggerDependencies(
        triggerProperties: TumblingWindowTriggerProperties,
        val: TriggerDependency[],
        violation?: IViolation,
        context?: IValidationContext
    ): Promise<IViolation[]> {
        if (tumblingWindowTriggerValidators.validateTriggerDependencies) {
            return tumblingWindowTriggerValidators.validateTriggerDependencies(triggerProperties, context);
        }
        return undefined;
    }

    private static validateInterval(triggerProperties: TumblingWindowTriggerProperties, interval: number): IViolation[] | undefined {
        const freq = triggerProperties.frequency;
        if (freq === 'Minute' && interval < 5) {
            return [
                {
                    message: () => shellStringResources.Trigger_TumblingInvalidIntervalError
                }
            ];
        }
        if (freq && interval) {
            switch (freq) {
                case 'Hour': return interval > 12000 ?
                    [{
                        message: () => shellStringResources.Trigger_Interval_Hour
                    }]
                    : undefined;
                case 'Minute': return interval > 720000 ?
                    [{
                        message: () => shellStringResources.Trigger_Interval_Minute
                    }]
                    : undefined;
                default:
                    return undefined;
            }
        }
        return undefined;
    }

    public setRecurrence(frequency: TriggerFrequency, startTime: Date, interval: number, delay: string, maxConcurrency: number, endTime?: Date, retryPolicy?: RetryPolicy): void {
        this.frequency = frequency;
        this.interval = interval;
        this.startTime = startTime;
        this.endTime = endTime;
        this.retryPolicy = retryPolicy;
        this.delay = delay;
        this.maxConcurrency = maxConcurrency;
    }
}
